import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const INCLUDED = [
  "Full personal assessment",
  "Personalised career plan, updated monthly",
  "Weekly step‑by‑step action prompts",
  "Skill & role recommendations",
  "Private dashboard to track progress",
];

const ARCHIVE_INCLUDED = [
  "Your assessment & report kept on file",
  "Download your PDF résumé any time",
  "Pause active job-matching without losing your work",
  "Return later — no need to start over",
  "One annual charge, no monthly billing",
];

export default function PricingPreview() {
  return (
    <section
      id="pricing"
      className="bg-white py-24 lg:py-32"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
            Pricing
          </p>
          <h2
            className="mt-4 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-slate-900"
            data-testid="pricing-heading"
          >
            Two simple plans. Pay only for what you need.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            {"Active career change isn't a weekend project — we ask for at least three months. When you're ready to step back, switch to My Archive and we'll keep your work safe."}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Membership */}
          <div
            className="relative bg-white border border-slate-200 shadow-xl rounded-3xl p-8 lg:p-10"
            data-testid="pricing-card"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-medium text-white">
              Membership
            </div>

            <div className="text-center">
              <h3 className="text-xl font-medium text-slate-900">Career Reset</h3>
              <div className="mt-4 flex items-baseline justify-center gap-1">
                <span
                  className="text-5xl font-medium text-slate-900 tracking-tight"
                  data-testid="pricing-amount"
                >
                  $50
                </span>
                <span className="text-base text-slate-500">/ month + GST</span>
              </div>
              <p
                className="mt-1 text-xs text-slate-500"
                data-testid="pricing-amount-inc-gst"
              >
                $55 incl. GST per month
              </p>
              <p
                className="mt-3 text-sm text-slate-500"
                data-testid="pricing-commitment"
              >
                Minimum 3-month commitment · Cancel anytime after that or switch to My Archive
              </p>
            </div>

            <ul className="mt-8 space-y-3.5">
              {INCLUDED.map((item, i) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm lg:text-base text-slate-700"
                  data-testid={`pricing-feature-${i + 1}`}
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                    <Check className="h-3 w-3" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <Link
              to="/register"
              className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3.5 text-base font-medium text-white shadow-md hover:bg-emerald-700 hover:-translate-y-0.5 transition-all"
              data-testid="pricing-cta-assessment-button"
            >
              Start My Assessment
              <ArrowRight className="h-4 w-4" />
            </Link>

            <p className="mt-4 text-center text-xs text-slate-500">
              No card required to start the assessment.
            </p>
          </div>

          {/* Archive */}
          <div
            className="relative bg-white border border-slate-200 shadow-xl rounded-3xl p-8 lg:p-10"
            data-testid="pricing-archive-card"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white">
              My Archive
            </div>

            <div className="text-center">
              <h3 className="text-xl font-medium text-slate-900">
                Keep your data on file
              </h3>
              <div className="mt-4 flex items-baseline justify-center gap-1">
                <span
                  className="text-5xl font-medium text-slate-900 tracking-tight"
                  data-testid="pricing-archive-amount"
                >
                  $10
                </span>
                <span className="text-base text-slate-500">/ month + GST</span>
              </div>
              <p
                className="mt-1 text-xs text-slate-500"
                data-testid="pricing-archive-amount-inc-gst"
              >
                $120 + GST = $132 incl. GST per year, paid once
              </p>
              <p className="mt-3 text-sm text-slate-500">
                Available after your first 3 months of membership
              </p>
            </div>

            <ul className="mt-8 space-y-3.5">
              {ARCHIVE_INCLUDED.map((item, i) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm lg:text-base text-slate-700"
                  data-testid={`pricing-archive-feature-${i + 1}`}
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                    <Check className="h-3 w-3" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <Link
              to="/subscribe"
              className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-6 py-3.5 text-base font-medium text-white shadow-md hover:bg-amber-700 hover:-translate-y-0.5 transition-all"
              data-testid="pricing-cta-archive-button"
            >
              See Archive details
              <ArrowRight className="h-4 w-4" />
            </Link>

            <p className="mt-4 text-center text-xs text-slate-500">
              Renews yearly. Cancel anytime — access continues until your year ends.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
